/*
 * SPDX-License-Identifier: Apache-2.0
 *
 * The OpenSearch Contributors require contributions made to
 * this file be licensed under the Apache-2.0 license or a
 * compatible open source license.
 *
 * Modifications Copyright OpenSearch Contributors. See
 * GitHub history for details.
 */

/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import React from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { OuiScreenReaderOnly } from '../accessibility';
import { OuiIcon } from '../icon';
import { resolveWidthAsStyle } from './utils';
import { OuiInnerText } from '../inner_text';
import { LEFT_ALIGNMENT, RIGHT_ALIGNMENT, CENTER_ALIGNMENT } from '../../services';
import { OuiI18n } from '../i18n';
var CellContents = function CellContents(_ref) {
  var className = _ref.className,
    description = _ref.description,
    children = _ref.children,
    isSorted = _ref.isSorted,
    isSortAscending = _ref.isSortAscending,
    showSortMsg = _ref.showSortMsg;
  return /*#__PURE__*/React.createElement("span", {
    className: className
  }, /*#__PURE__*/React.createElement(OuiInnerText, null, function (ref, innerText) {
    return /*#__PURE__*/React.createElement(OuiI18n, {
      token: "ouiTableHeaderCell.titleTextWithDesc",
      default: "{innerText}; {description}",
      values: {
        innerText: innerText,
        description: description
      }
    }, function (titleTextWithDesc) {
      return /*#__PURE__*/React.createElement("span", {
        title: description ? titleTextWithDesc : innerText,
        ref: ref,
        className: "ouiTableCellContent__text"
      }, children);
    });
  }), description && /*#__PURE__*/React.createElement(OuiScreenReaderOnly, null, /*#__PURE__*/React.createElement("span", null, description)), showSortMsg && isSorted && /*#__PURE__*/React.createElement(OuiIcon, {
    className: "ouiTableSortIcon",
    type: isSortAscending ? 'sortUp' : 'sortDown',
    size: "m"
  }));
};
export var OuiTableHeaderCell = ({
  children,
  align = LEFT_ALIGNMENT,
  onSort,
  isSorted,
  isSortAscending,
  className,
  scope = 'col',
  mobileOptions = {
    show: true
  },
  width,
  style,
  readOnly,
  // Soon to be deprecated for {...mobileOptions}
  isMobileHeader,
  hideForMobile,
  description,
  ...rest
}) => {
  const classes = classNames('ouiTableHeaderCell', className, {
    'ouiTableHeaderCell--hideForDesktop': mobileOptions.only || isMobileHeader,
    'ouiTableHeaderCell--hideForMobile': !mobileOptions.show || hideForMobile
  });
  const contentClasses = classNames('ouiTableCellContent', className, {
    'ouiTableCellContent--alignRight': align === RIGHT_ALIGNMENT,
    'ouiTableCellContent--alignCenter': align === CENTER_ALIGNMENT
  });
  const styleObj = resolveWidthAsStyle(style, width);
  const CellComponent = children ? 'th' : 'td';
  if (onSort || isSorted) {
    const buttonClasses = classNames('ouiTableHeaderButton', {
      'ouiTableHeaderButton-isSorted': isSorted
    });
    let ariaSortValue = 'none';
    if (isSorted) {
      ariaSortValue = isSortAscending ? 'ascending' : 'descending';
    }
    const cellContents = <CellContents className={contentClasses} description={description} showSortMsg={true} children={children} isSorted={isSorted} isSortAscending={isSortAscending} />;
    return <CellComponent className={classes} scope={scope} role="columnheader" aria-sort={ariaSortValue} aria-live="polite" style={styleObj} {...rest}>
        {onSort && !readOnly ? <button type="button" className={buttonClasses} onClick={onSort} data-test-subj="tableHeaderSortButton">
            {cellContents}
          </button> : cellContents}
      </CellComponent>;
  }
  return <CellComponent className={classes} scope={scope} role="columnheader" style={styleObj} {...rest}>
      <CellContents className={contentClasses} description={description} showSortMsg={false} children={children} isSorted={isSorted} isSortAscending={isSortAscending} />
    </CellComponent>;
};
OuiTableHeaderCell.propTypes = {
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  align: PropTypes.oneOf(["left", "right", "center"]),
  /**
       * _DEPRECATED: use `mobileOptions.show = false`_ Indicates if the
       * column should not show for mobile users (typically hidden because a
       * custom mobile header utilizes the column's contents)
       */
  hideForMobile: PropTypes.bool,
  /**
       * _DEPRECATED: use `mobileOptions.only = true`_ Indicates if the
       * column was created to be the row's heading in mobile view (this
       * column will be hidden at larger screens)
       */
  isMobileHeader: PropTypes.bool,
  isSortAscending: PropTypes.bool,
  isSorted: PropTypes.bool,
  /**
       * Mobile options for displaying differently at small screens
       */
  mobileOptions: PropTypes.shape({
    /**
           * If false, will not render the column at all for mobile
           */
    show: PropTypes.bool,
    /**
           * Only show for mobile? If true, will not render the column at all
           * for desktop
           */
    only: PropTypes.bool
  }),
  onSort: PropTypes.func,
  scope: PropTypes.oneOf(["col", "row", "colgroup", "rowgroup"]),
  width: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.number.isRequired]),
  description: PropTypes.string,
  /**
       * Shows the sort indicator but removes the button
       */
  readOnly: PropTypes.bool
};
try {
  OuiTableHeaderCell.__docgenInfo = {
    tags: {},
    filePath: '/codebuild/output/src3672379046/src/src/components/table/table_header_cell.tsx',
    description: '',
    displayName: 'OuiTableHeaderCell',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n' + '@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      align: {
        defaultValue: {
          value: 'left'
        },
        description: '',
        name: 'align',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/table/table_header_cell.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: 'HorizontalAlignment',
          value: [{
            value: '"left"'
          }, {
            value: '"right"'
          }, {
            value: '"center"'
          }]
        }
      },
      hideForMobile: {
        defaultValue: null,
        description: '_DEPRECATED: use `mobileOptions.show = false`_ Indicates if the\n' + 'column should not show for mobile users (typically hidden because a\n' + "custom mobile header utilizes the column's contents)",
        name: 'hideForMobile',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/table/table_header_cell.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isMobileHeader: {
        defaultValue: null,
        description: '_DEPRECATED: use `mobileOptions.only = true`_ Indicates if the\n' + "column was created to be the row's heading in mobile view (this\n" + 'column will be hidden at larger screens)',
        name: 'isMobileHeader',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/table/table_header_cell.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isSortAscending: {
        defaultValue: null,
        description: '',
        name: 'isSortAscending',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/table/table_header_cell.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isSorted: {
        defaultValue: null,
        description: '',
        name: 'isSorted',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/table/table_header_cell.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      mobileOptions: {
        defaultValue: {
          value: '{\n    show: true,\n  }'
        },
        description: 'Mobile options for displaying differently at small screens',
        name: 'mobileOptions',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/table/table_header_cell.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: '{ show?: boolean; only?: boolean; }'
        }
      },
      onSort: {
        defaultValue: null,
        description: '',
        name: 'onSort',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/table/table_header_cell.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'NoArgCallback<void>'
        }
      },
      scope: {
        defaultValue: {
          value: 'col'
        },
        description: '',
        name: 'scope',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/table/table_header_cell.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: 'TableHeaderCellScope',
          value: [{
            value: '"col"'
          }, {
            value: '"colgroup"'
          }, {
            value: '"row"'
          }, {
            value: '"rowgroup"'
          }]
        }
      },
      width: {
        defaultValue: null,
        description: '',
        name: 'width',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/table/table_header_cell.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string | number'
        }
      },
      description: {
        defaultValue: null,
        description: '',
        name: 'description',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/table/table_header_cell.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      readOnly: {
        defaultValue: null,
        description: 'Shows the sort indicator but removes the button',
        name: 'readOnly',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/table/table_header_cell.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      }
    },
    extendedInterfaces: ['CommonProps', 'HTMLAttributes', 'ThHTMLAttributes', 'AriaAttributes', 'DOMAttributes']
  };
} catch (e) {}